import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingGeorgePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'George Professional Painters - Interior & Exterior Painting | Garden Route Specialists',
    description: 'Expert painting services in George, Wilderness, and Mossel Bay. Specialized in interior, exterior, and commercial painting. Over 14 years serving the Garden Route with quality workmanship.',
    keywords: 'painters George, house painters George, interior painting George, exterior painting, commercial painting, Garden Route painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters George',
          description: 'Professional painting services in George and Garden Route',
          address: 'George, Western Cape',
          priceRange: '$$',
          serviceArea: ['George', 'Wilderness', 'Mossel Bay', 'Knysna', 'Oudtshoorn']
        }),
        generateServiceSchema({
          name: 'Garden Route Painting Services',
          description: 'Interior, exterior, and commercial painting',
          provider: 'Painters George',
          areaServed: ['George', 'Wilderness', 'Mossel Bay'],
          serviceType: ['Interior Painting', 'Exterior Painting', 'Commercial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters George",
    location: "George, Garden Route",
    website: "https://paintersgeorge.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/5094f6157a1fa5d11859ed617549bbbf.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20house%20exterior%20in%20George%20Western%20Cape%20with%20mountain%20views%20garden%20route%20architecture%20white%20walls%20dark%20trim%20professional%20finish&width=800&height=600&seq=gallery-george1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20project%20in%20George%20home%20elegant%20living%20space%20with%20feature%20wall%20modern%20furniture%20natural%20lighting%20professional%20paint%20finish&width=800&height=600&seq=gallery-george2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=exterior%20house%20painting%20in%20George%20Garden%20Route%20area%20with%20scaffolding%20professional%20painters%20working%20on%20facade%20mountain%20backdrop&width=800&height=600&seq=gallery-george3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=roof%20coating%20project%20in%20George%20Western%20Cape%20residential%20property%20with%20mountain%20views%20professional%20waterproofing%20application%20clean%20finish&width=800&height=600&seq=gallery-george4&orientation=landscape"
    ],
    services: [
      {
        title: "Interior Painting",
        description: "Transform your George home's interior with our expert painting services. We specialize in creating beautiful, long-lasting finishes that enhance your living spaces and reflect your personal style.",
        features: ["Living rooms & bedrooms", "Kitchens & bathrooms", "Feature walls & accent colors", "Ceiling painting", "Trim & skirting boards", "Color consultation"]
      },
      {
        title: "Exterior Painting",
        description: "Protect and beautify your property's exterior with our professional painting services. We use premium weather-resistant paints specifically chosen for the Garden Route climate conditions.",
        features: ["House facades & walls", "Weatherboard & cladding", "Fascias & soffits", "Garden walls & fences", "Garage doors", "Pressure washing & preparation"]
      },
      {
        title: "Roof Coating",
        description: "Extend the life of your roof with our specialized coating services. Our waterproofing solutions protect against the Garden Route's variable weather while improving your property's appearance.",
        features: ["Tile roof coating", "Metal roof painting", "Waterproofing treatments", "Leak repairs", "Gutter painting", "Heat-reflective coatings"]
      }
    ],
    serviceAreas: ["George", "Denneoord", "Blanco", "Heatherlands", "Wilderness", "Victoria Bay", "Herolds Bay", "Pacaltsdorp", "Uniondale"],
    about: "Painters George is the Garden Route's premier painting company, serving George and surrounding areas for over 16 years. We have established ourselves as the most trusted and reliable painting contractors in the region, known for our exceptional quality, attention to detail, and commitment to customer satisfaction.",
    aboutExtended: [
      "Our team of highly skilled and experienced painters specializes in both residential and commercial projects throughout the Garden Route. We understand the unique challenges posed by the local climate, from coastal humidity to mountain weather patterns, and use only premium paints and materials specifically selected to withstand these conditions.",
      "What distinguishes Painters George is our deep understanding of local architecture and building styles, from traditional Garden Route cottages to modern contemporary homes. We take pride in preserving the character of heritage properties while bringing fresh, modern appeal to newer constructions.",
      "We believe in building lasting relationships with our clients through transparent communication, reliable service, and exceptional workmanship. Every project, whether a single room refresh or a complete property transformation, receives the same level of professional care and attention to detail. Our comprehensive approach includes thorough surface preparation, premium materials, expert application, and meticulous cleanup."
    ],
    ourApproach: [
      "Free on-site consultation and detailed quotation",
      "Comprehensive surface preparation and repairs",
      "Premium quality paints suited to Garden Route climate",
      "Professional application by certified painters",
      "Minimal disruption to your daily routine",
      "Thorough cleanup and waste removal",
      "Final inspection and quality assurance",
      "Post-project support and warranty"
    ],
    whyChoose: [
      "16+ years serving the Garden Route",
      "Fully licensed and insured",
      "Local climate expertise",
      "Premium quality materials",
      "Free color consultation",
      "Competitive pricing",
      "Punctual and reliable",
      "Satisfaction guaranteed",
      "Heritage property specialists",
      "Eco-friendly paint options"
    ],
    testimonials: [
      {
        name: "David Thompson",
        rating: 5,
        text: "Excellent service from start to finish! The team was professional, efficient, and the quality of work is outstanding. Our home in Heatherlands looks absolutely stunning. Highly recommend Painters George!"
      },
      {
        name: "Susan Marais",
        rating: 5,
        text: "We've used Painters George for multiple projects over the years. They never disappoint! Professional, reliable, and the attention to detail is exceptional. Best painters in George by far."
      },
      {
        name: "James Wilson",
        rating: 5,
        text: "Fantastic experience! They completed our exterior painting project ahead of schedule and the results exceeded our expectations. Great value for money and wonderful customer service."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters George</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters George</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in the Garden Route region:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer a comprehensive range of painting services tailored to meet the unique needs of Garden Route properties.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">George, Wilderness, Herolds Bay</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7:30AM-5PM</p>
                    <p className="text-gray-600">Sat: 8AM-12PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>George</li>
                <li>Wilderness</li>
                <li>Herolds Bay</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
